/**
 * One rule for an athlete's standing, so roster status, transactions and
 * the contract's pay strip agree.
 *
 * Pay is priced week by week from the open roster period on the active
 * playing contract. `clients.roster_status` is what the world last said
 * about the athlete. Every writer of either (the roster sync, an ESPN
 * transaction, a person in the app, the MCP) decides through
 * `planStandingChange` what the open period should be and writes both in
 * the same operation. Every period carries provenance, so a wrong one can
 * be traced to the sentence or roster read that produced it.
 *
 * Precedence: a dated ESPN transaction supersedes anything and upgrades an
 * approximate period's date; a roster-sync detection supersedes automated
 * periods but never a manual one; a manual write supersedes everything (a
 * person is acting on what they see). A hand-set period against a later
 * sync observation is never resolved silently: it is `blocked_manual`,
 * which Data Health and a weekly notification surface.
 *
 * Pure. Shared by the app, the MCP (web/api/mcp.ts) and the scraper, which
 * carries a generated copy (scraper-api/src/shared, see
 * scraper-api/scripts/sync-shared.mjs). No I/O, no imports.
 */

export type PayState = 'active_roster' | 'injured_reserve' | 'pup' | 'suspended' | 'practice_squad' | 'free_agent'
export type StandingSource = 'espn_transaction' | 'roster_sync' | 'manual' | 'backfill'

export type MoveKind =
  | 'signed' | 'released' | 'waived' | 'traded' | 'placed_ir' | 'placed_pup' | 'activated' | 'suspended'
  | 'reinstated' | 'retired' | 'elevated' | 'extension' | 'restructure' | 'franchise_tagged' | 'transition_tagged'

/** The period fields the rules read and write; structurally a ContractRosterPeriod. */
export interface StandingPeriod {
  id: string
  status: PayState
  start_date: string
  end_date: string | null
  weeks?: number | null
  games?: number | null
  weekly_amount?: number | null
  per_game_amount?: number | null
  notes?: string | null
  source?: StandingSource | null
  /** The player_transactions row this period came from. */
  transaction_id?: string | null
  /** True when start_date is the day the move was noticed, not the day it happened. */
  approximate?: boolean | null
  recorded_at?: string | null
  /** Vested-veteran termination pay confirmed on a free_agent period: base / 18 keeps paying through week 18. */
  termination_pay?: boolean | null
  termination_pay_confirmed?: { by: string; at: string } | null
  /** Game dates a practice-squad player was elevated for; those weeks pay the active-roster check. */
  elevations?: string[] | null
}

/** Contract types whose roster periods price pay. */
export const PLAYING_CONTRACT_TYPES = ['playing', 'rookie', 'extension', 'free_agent', 'restructure', 'franchise_tag', 'transition_tag', 'tender'] as const

export function isPlayingContract(c: { contract_type?: string | null }): boolean {
  const t = String(c.contract_type ?? '').trim().toLowerCase()
  // A blank type on an older contract was a playing contract; the field
  // arrived after those rows did.
  return t === '' || (PLAYING_CONTRACT_TYPES as readonly string[]).includes(t)
}

/**
 * The PocketBase filter for a contract whose pay counts, built from the app's
 * own list (shared/standingRules) rather than spelled out again.
 *
 * The money pages used to ask for contract_type = "playing" alone, while the
 * pay strip, the data-health checks, the status sync and the OTC linker all
 * used isPlayingContract — which also counts a rookie deal, an extension, a
 * tender and the rest. On JB that one word hid 15 of 20 active contracts: ten
 * rostered athletes had their salary on file and no money anywhere on the
 * Money page.
 */
export function playingContractFilter(): string {
  // A blank type belongs to a row written before the field existed.
  return [...PLAYING_CONTRACT_TYPES, ''].map(t => `contract_type = "${t}"`).join(' || ')
}

/** An NFL client roster_status → pay state. Null means the value has no pay meaning (futures, college, blank). */
export function payStateForRosterStatus(rs: string | null | undefined): PayState | null {
  switch (String(rs ?? '').trim().toLowerCase()) {
    case 'active': return 'active_roster'
    case 'ir': return 'injured_reserve'
    case 'pup': return 'pup'
    case 'suspended': return 'suspended'
    case 'practice_squad': return 'practice_squad'
    case 'free_agent':
    case 'waived':
    case 'retired': return 'free_agent'
    default: return null
  }
}

/**
 * The status badge (clients.status) a roster status implies, or null to leave
 * the badge as it is.
 *
 * The badge and roster_status grew up separately, so a player on the Lions
 * could read "Free agent" at the top of his page while a cut player read
 * "Active". The badge only follows while it holds a standing value (blank,
 * active, free_agent, injured); signed, prospect, former, nil, inactive and
 * retired say something about the relationship that ESPN cannot, and are
 * never touched. A person's "injured" survives IR and PUP, which say the same.
 */
export function badgeForRosterStatus(
  badge: string | null | undefined,
  rosterStatus: string | null | undefined,
): 'active' | 'free_agent' | 'retired' | null {
  const current = String(badge ?? '').trim().toLowerCase()
  if (!['', 'active', 'free_agent', 'injured'].includes(current)) return null
  const rs = String(rosterStatus ?? '').trim().toLowerCase()
  let want: 'active' | 'free_agent' | 'retired' | 'injured'
  if (['active', 'ir', 'pup', 'suspended', 'practice_squad'].includes(rs)) {
    want = current === 'injured' && (rs === 'ir' || rs === 'pup') ? 'injured' : 'active'
  } else if (rs === 'free_agent' || rs === 'waived') {
    want = 'free_agent'
  } else if (rs === 'retired') {
    want = 'retired'
  } else {
    return null
  }
  return want === current || want === 'injured' ? null : want
}

/** Pay state → the roster_status to write. `current` is kept when it already means the same thing (waived, retired). */
export function rosterStatusForPayState(ps: PayState, current: string | null | undefined): string {
  if (ps === 'free_agent') {
    const c = String(current ?? '').toLowerCase()
    return c === 'waived' || c === 'retired' ? c : 'free_agent'
  }
  switch (ps) {
    case 'active_roster': return 'active'
    case 'injured_reserve': return 'ir'
    case 'pup': return 'pup'
    case 'suspended': return 'suspended'
    case 'practice_squad': return 'practice_squad'
  }
}

export type MoveMeaning =
  | { kind: 'state'; payState: PayState; rosterStatus: string }
  | { kind: 'club_only' }
  | { kind: 'contract_event' }
  | { kind: 'elevation' }

/** What a transaction means for standing. `psDirection` is where "practice squad" appeared in the sentence. */
export function standingForMove(move: MoveKind, psDirection: 'to' | 'from' | null): MoveMeaning {
  switch (move) {
    case 'signed':
      return psDirection === 'to'
        ? { kind: 'state', payState: 'practice_squad', rosterStatus: 'practice_squad' }
        : { kind: 'state', payState: 'active_roster', rosterStatus: 'active' }
    case 'activated':
    case 'reinstated':
      return { kind: 'state', payState: 'active_roster', rosterStatus: 'active' }
    case 'placed_ir': return { kind: 'state', payState: 'injured_reserve', rosterStatus: 'ir' }
    case 'placed_pup': return { kind: 'state', payState: 'pup', rosterStatus: 'pup' }
    case 'suspended': return { kind: 'state', payState: 'suspended', rosterStatus: 'suspended' }
    case 'released': return { kind: 'state', payState: 'free_agent', rosterStatus: 'free_agent' }
    case 'waived': return { kind: 'state', payState: 'free_agent', rosterStatus: 'waived' }
    case 'retired': return { kind: 'state', payState: 'free_agent', rosterStatus: 'retired' }
    case 'traded': return { kind: 'club_only' }
    case 'elevated': return { kind: 'elevation' }
    case 'extension':
    case 'restructure':
    case 'franchise_tagged':
    case 'transition_tagged':
      return { kind: 'contract_event' }
  }
}

/** The transaction type a standing change is recorded as, for rows the sync or the MCP write. */
export function moveForStandingChange(from: PayState | null, to: PayState): MoveKind {
  switch (to) {
    case 'practice_squad': return 'signed'
    case 'active_roster':
      return from === 'injured_reserve' || from === 'pup' || from === 'suspended' ? 'activated' : 'signed'
    case 'injured_reserve': return 'placed_ir'
    case 'pup': return 'placed_pup'
    case 'suspended': return 'suspended'
    case 'free_agent': return 'released'
  }
}

export const MOVE_LABEL: Record<MoveKind, string> = {
  signed: 'Signed', released: 'Released', waived: 'Waived', traded: 'Traded', placed_ir: 'Placed on IR',
  placed_pup: 'Placed on PUP', activated: 'Activated', suspended: 'Suspended', reinstated: 'Reinstated',
  retired: 'Retired', elevated: 'Elevated from the practice squad', extension: 'Extension', restructure: 'Restructure',
  franchise_tagged: 'Franchise tagged', transition_tagged: 'Transition tagged',
}

/**
 * Where a period came from. Rows written before provenance existed carry
 * the older markers: an `espn-…` id or a "From ESPN" note. Anything else
 * was a person.
 */
export function sourceOf(p: Pick<StandingPeriod, 'id' | 'notes' | 'source'>): StandingSource {
  if (p.source) return p.source
  if (String(p.id ?? '').startsWith('espn-') || String(p.notes ?? '').startsWith('From ESPN')) return 'espn_transaction'
  return 'manual'
}

/** Open periods, latest start first. */
export function openPeriods(history: StandingPeriod[] | null | undefined): StandingPeriod[] {
  return (Array.isArray(history) ? history : [])
    .filter(p => p && p.end_date == null)
    .sort((a, b) => String(b.start_date).localeCompare(String(a.start_date)))
}

export function periodId(source: StandingSource, key: string): string {
  const prefix = source === 'espn_transaction' ? 'tx' : source === 'roster_sync' ? 'sync' : source === 'backfill' ? 'bf' : 'man'
  return `${prefix}-${key.replace(/[^a-z0-9]/gi, '').slice(-16)}`
}

export interface StandingEvent {
  /** Null when the event carries no pay meaning (a college value, a club-only move). */
  payState: PayState | null
  /** The roster_status label to write; null leaves it alone. */
  rosterStatus: string | null
  source: StandingSource
  /** YYYY-MM-DD */
  date: string
  approximate: boolean
  transactionId?: string | null
  note: string
  club?: string | null
}

export interface StandingContext {
  client: { roster_status: string | null | undefined }
  /** The one active playing contract, or null. */
  contract: { id: string; roster_history: StandingPeriod[] | null | undefined } | null
  /** YYYY-MM-DD */
  today: string
}

export type StandingOutcome =
  | 'applied' | 'agree' | 'provenance_upgraded' | 'label_only' | 'no_contract'
  | 'unmapped' | 'blocked_manual' | 'stale' | 'open_conflict'

export interface StandingPlan {
  outcome: StandingOutcome
  reason: string
  /** Fields to write on the client, or null for nothing. */
  client: { roster_status?: string; team?: string; team_name?: string } | null
  /** The whole roster_history to write, or null for nothing. */
  contract: { roster_history: StandingPeriod[] } | null
}

function clientPatch(ev: StandingEvent, ctx: StandingContext): StandingPlan['client'] {
  const out: NonNullable<StandingPlan['client']> = {}
  if (ev.rosterStatus && ev.rosterStatus !== String(ctx.client.roster_status ?? '')) out.roster_status = ev.rosterStatus
  if (ev.club) { out.team = ev.club; out.team_name = ev.club }
  return Object.keys(out).length ? out : null
}

function newPeriod(ev: StandingEvent, payState: PayState, key: string): StandingPeriod {
  return {
    id: periodId(ev.source, key),
    status: payState,
    start_date: ev.date,
    end_date: null,
    weeks: null,
    games: null,
    weekly_amount: null,
    per_game_amount: null,
    notes: ev.note,
    source: ev.source,
    transaction_id: ev.transactionId ?? null,
    approximate: ev.approximate,
    recorded_at: new Date().toISOString(),
  }
}

/**
 * The decision. See the table in the plan: the event's source against the
 * open period's source decides whether the strip moves.
 */
export function planStandingChange(ctx: StandingContext, ev: StandingEvent): StandingPlan {
  const client = clientPatch(ev, ctx)
  if (!ev.payState) {
    return { outcome: client ? 'label_only' : 'unmapped', reason: 'the event carries no pay state', client, contract: null }
  }
  if (!ctx.contract) {
    return { outcome: 'no_contract', reason: 'no active playing contract to carry a period', client, contract: null }
  }
  const history = Array.isArray(ctx.contract.roster_history) ? ctx.contract.roster_history.filter(Boolean) : []
  const open = openPeriods(history)
  if (open.length > 1) {
    return { outcome: 'open_conflict', reason: `${open.length} periods are open; nothing written until one is closed`, client: null, contract: null }
  }
  const key = ev.transactionId ?? `${ev.date}-${ev.payState}`
  const cur = open[0] ?? null

  if (!cur) {
    return { outcome: 'applied', reason: 'no open period; one opened', client, contract: { roster_history: [...history, newPeriod(ev, ev.payState, key)] } }
  }
  const curSrc = sourceOf(cur)

  if (cur.status === ev.payState) {
    if (ev.source === 'espn_transaction' && curSrc !== 'espn_transaction') {
      const upgraded: StandingPeriod = {
        ...cur,
        start_date: ev.date < cur.start_date ? ev.date : cur.start_date,
        source: 'espn_transaction',
        transaction_id: ev.transactionId ?? cur.transaction_id ?? null,
        approximate: false,
        notes: cur.notes ? `${cur.notes} · ${ev.note}` : ev.note,
      }
      return {
        outcome: 'provenance_upgraded', reason: 'same state; the ESPN transaction dates the open period',
        client, contract: { roster_history: history.map(p => (p.id === cur.id ? upgraded : p)) },
      }
    }
    return { outcome: 'agree', reason: 'the open period already says so', client, contract: null }
  }

  if (ev.source === 'roster_sync') {
    if (curSrc === 'manual') {
      return { outcome: 'blocked_manual', reason: `the open ${cur.status} period was set by hand; the roster sync does not overrule a person`, client, contract: null }
    }
    if (cur.start_date > ev.date) {
      return { outcome: 'stale', reason: `the open period starts ${cur.start_date}, after this observation`, client: null, contract: null }
    }
  }

  let closeAt = ev.date
  let closedNote = cur.notes ?? null
  if (closeAt < cur.start_date) {
    closeAt = cur.start_date
    closedNote = `${closedNote ? `${closedNote} · ` : ''}closed on its own start: the move was dated before the period began`
  }
  const closed: StandingPeriod = { ...cur, end_date: closeAt, notes: closedNote }
  const next = newPeriod(ev, ev.payState, key)
  return {
    outcome: 'applied',
    reason: `${cur.status} closed ${closeAt}; ${ev.payState} opened`,
    client,
    contract: { roster_history: [...history.map(p => (p.id === cur.id ? closed : p)), next] },
  }
}

/** An elevation: the game date is recorded on the open practice-squad period; the strip's state does not change. */
export function planElevation(
  ctx: StandingContext,
  gameDate: string,
): { outcome: 'applied' | 'agree' | 'no_contract' | 'not_on_practice_squad' | 'over_cap'; contract: { roster_history: StandingPeriod[] } | null; count: number } {
  if (!ctx.contract) return { outcome: 'no_contract', contract: null, count: 0 }
  const history = Array.isArray(ctx.contract.roster_history) ? ctx.contract.roster_history.filter(Boolean) : []
  const cur = openPeriods(history)[0]
  if (!cur || cur.status !== 'practice_squad') return { outcome: 'not_on_practice_squad', contract: null, count: 0 }
  const have = Array.isArray(cur.elevations) ? cur.elevations : []
  if (have.includes(gameDate)) return { outcome: 'agree', contract: null, count: have.length }
  const elevations = [...have, gameDate].sort()
  const updated = { ...cur, elevations }
  return {
    outcome: elevations.length > ELEVATION_CAP ? 'over_cap' : 'applied',
    contract: { roster_history: history.map(p => (p.id === cur.id ? updated : p)) },
    count: elevations.length,
  }
}

/** Standard elevations allowed per player per season under the CBA. */
export const ELEVATION_CAP = 3

export type StandingMismatch =
  | { kind: 'label_vs_period'; label: string; labelState: PayState; period: StandingPeriod }
  | { kind: 'multiple_open'; periods: StandingPeriod[] }
  | { kind: 'no_open_period'; label: string; labelState: PayState }

/**
 * Do the client's label and the contract's open period agree? For Data
 * Health and the repair script. Null when they do, or when the label has no
 * pay meaning.
 */
export function standingMismatch(
  client: { roster_status: string | null | undefined },
  contract: { roster_history: StandingPeriod[] | null | undefined },
): StandingMismatch | null {
  const labelState = payStateForRosterStatus(client.roster_status)
  const open = openPeriods(contract.roster_history)
  if (open.length > 1) return { kind: 'multiple_open', periods: open }
  if (!labelState) return null
  const label = String(client.roster_status)
  if (open.length === 0) return { kind: 'no_open_period', label, labelState }
  if (open[0].status !== labelState) return { kind: 'label_vs_period', label, labelState, period: open[0] }
  return null
}
